import { useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import {
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  FileText,
  ShieldAlert,
  Sparkles
} from 'lucide-react'
import CustomButton from '../../../../ui/CustomButton'
import ChangePlanModal from '../../../../ui/ChangePlanModal'
import ContractAddonsModal from '../../../../ui/ContractAddonsModal'
import CredentialForm from './CredencialForm'

const ONBOARDING_STEPS = [
  {
    id: 'intro',
    label: 'Introduccion',
    title: 'Activa tu facturacion inteligente en TryLuca',
    subtitle: 'Este módulo te permite emitir facturas sin salir de TryLuca, conectándote directamente con el facturador del SII.'
  },
  {
    id: 'activation',
    label: 'Activacion',
    title: 'Activa el modulo de facturacion',
    subtitle: 'Para acceder a la facturación masiva y programada, necesitas activar este módulo en tu cuenta.'
  },
  {
    id: 'credential',
    label: 'Credenciales',
    title: 'Conecta tu credencial del SII',
    subtitle: 'Completa los datos requeridos para terminar la configuracion del modulo.'
  }
]

const STEP_INDEX = {
  intro: 0,
  activation: 1,
  credential: 2
}

const FEATURE_CARDS = [
  {
    title: 'Facturacion masiva',
    description: [
      'Carga multiples documentos en segundos.',
      'Emite todas tus facturas de una sola vez.',
      'Olvidate de hacer una por una.'
    ],
    icon: FileText
  },
  {
    title: 'Facturacion programada',
    description: [
      'Configura facturas recurrentes.',
      'Se emiten automaticamente en la fecha definida.',
      'Sin recordatorios, sin olvidos y sin trabajo manual.'
    ],
    icon: CalendarClock
  }
]

const ACTIVATION_OPTIONS = [
  {
    key: 'addon',
    tag: 'Opcion 1',
    title: 'Contratar add-on',
    description:
      'Activa el modulo sin cambiar tu plan actual cuando esa modalidad este disponible para tu organizacion.',
    buttonLabel: 'Contratar add-on'
  },
  {
    key: 'plan',
    tag: 'Opcion 2',
    title: 'Ver planes disponibles',
    description:
      'Revisa los planes que ya incluyen facturacion inteligente y completa la activacion dentro del mismo flujo.',
    buttonLabel: 'Ver planes disponibles'
  }
]

const ACTIVATION_CONTEXT_CONTENT = {
  default: {
    eyebrow: 'Ruta de activacion',
    title: 'Elige como habilitar el modulo',
    description:
      'Puedes activar facturacion desde un add-on o desde un cambio de plan, segun lo que mejor calce con tu organizacion.',
    items: [
      {
        title: 'Add-on',
        description: 'Mantienes tu plan actual y agregas solo la funcionalidad de facturacion.'
      },
      {
        title: 'Cambio de plan',
        description:
          'Actualizas tu suscripcion a un plan que ya incluye facturacion inteligente.'
      }
    ],
    footer: 'Cuando la activacion quede lista, se habilitara el siguiente paso del flujo.'
  },
  addon: {
    eyebrow: 'Seleccion add-on',
    title: 'Estas activando el modulo con un add-on',
    description:
      'Esta ruta es util cuando quieres sumar facturacion inteligente sin modificar el plan actual.',
    items: [
      {
        title: 'Mismo plan',
        description: 'La activacion se realiza sobre la suscripcion actual de tu organizacion.'
      },
      {
        title: 'Flujo integrado',
        description:
          'El pago se completa dentro del mismo producto y luego puedes volver a este onboarding.'
      }
    ],
    footer: 'Cuando termines la contratacion del add-on, este paso quedara listo para continuar.'
  },
  plan: {
    eyebrow: 'Contratación',
    title: 'Estas activando el modulo con un cambio de plan',
    description:
      'Usa esta ruta cuando quieras aprovechar un plan que ya venga con facturacion inteligente incluida.',
    items: [
      {
        title: 'Cambio guiado',
        description:
          'Seleccionas plan, tarjeta y confirmacion dentro del mismo flujo controlado.'
      },
      {
        title: 'Continuidad',
        description:
          'Al terminar podras volver a este onboarding para crear la credencial sin reiniciar el proceso.'
      }
    ],
    footer: 'Cuando el cambio de plan quede activo, podras avanzar al paso de credenciales.'
  },
  existing: {
    eyebrow: 'Estado actual',
    title: 'El modulo ya esta disponible',
    description:
      'La activacion del modulo ya existe para esta empresa, por lo que solo falta crear la credencial del SII.',
    items: [
      {
        title: 'Sin pasos extra',
        description: 'No necesitas contratar nada adicional para continuar con la configuracion.'
      },
      {
        title: 'Siguiente paso',
        description:
          'Solo debes completar la credencial para dejar habilitada la emision desde TryLuca.'
      }
    ],
    footer: 'Cuando quieras, avanza al ultimo paso para completar la conexion con el SII.'
  }
}

const CREDENTIAL_INFO_ITEMS = [
  {
    title: 'RUT del usuario',
    description:
      'Debe corresponder al rut del usuario autorizado para operar en el SII con la empresa actual. (Normalmente el del representante legal o del representante eléctronico autorizado)'
  },
  {
    title: 'Contrasena del SII',
    description: 'Es la clave que se usa para ingresar al portal del SII asociada al rut del usuario.'
  },
  {
    title: 'Contrasena del certificado',
    description:
      'Corresponde a la clave del certificado digital o firma electrónica que usas para emitir DTEs.'
  }
]

const StepHeader = ({ stepNumber, totalSteps, stepMeta }) => (
  <header className='w-full text-center'>
    <div className='mb-4 w-full lg:mb-5'>
      <div className='flex w-full items-center justify-center rounded-full bg-slate-100 px-4 py-2 transition-colors duration-200'>
        <span className='text-sm font-sans font-medium text-center text-slate-600 sm:text-base lg:text-lg'>
          Paso {stepNumber} de {totalSteps} - {stepMeta.label}
        </span>
      </div>
    </div>

    <div className='space-y-2 lg:space-y-3'>
      <h2 className='text-2xl font-sans font-bold tracking-tight text-slate-900 sm:text-3xl 2xl:text-4xl'>
        {stepMeta.title}
      </h2>
      <p className='w-full text-sm font-sans leading-6 text-slate-500 sm:text-base'>
        {stepMeta.subtitle}
      </p>
    </div>
  </header>
)

const SidePanelHeader = ({ eyebrow, title, description, inverse = false }) => (
  <div className='space-y-3'>
    <p
      className={`text-[11px] font-sans font-semibold uppercase tracking-[0.22em] ${
        inverse ? 'text-white/70' : 'text-accent2'
      }`}
    >
      {eyebrow}
    </p>
    <div className='space-y-2'>
      <h3 className={`text-2xl font-sans font-bold ${inverse ? 'text-white' : 'text-slate-900'}`}>
        {title}
      </h3>
      <p className={`text-sm font-sans leading-6 ${inverse ? 'text-white/80' : 'text-slate-600'}`}>
        {description}
      </p>
    </div>
  </div>
)

const InfoCard = ({ icon: Icon, title, description, tone = 'default', inverse = false }) => {
  const toneClasses = inverse
    ? 'border-white/15 bg-white/10 backdrop-blur-md'
    : tone === 'success'
      ? 'border-emerald-200 bg-emerald-50'
      : tone === 'warning'
        ? 'border-accent2/15 bg-gradient-to-br from-accent2/5 via-white to-white'
        : 'border-slate-200 bg-white'

  return (
    <article className={`rounded-[24px] border p-4 shadow-sm sm:p-5 ${toneClasses}`}>
      <div className='mb-3 flex items-center gap-3'>
        <div
          className={`flex h-12 w-12 items-center justify-center rounded-2xl ${
            inverse ? 'bg-white/15 text-white' : 'bg-accent2/10 text-accent2'
          }`}
        >
          <Icon className='h-5 w-5' />
        </div>
        <h4 className={`text-lg font-sans font-semibold ${inverse ? 'text-white' : 'text-slate-900'}`}>
          {title}
        </h4>
      </div>

      <ul className='space-y-1 text-sm font-sans text-white/80'>
      {description.map((item, index) => (
        <li key={index} className='flex items-start gap-2'>
          <span className='mt-1 h-1.5 w-1.5 rounded-full bg-accent2' />
          <span>{item}</span>
        </li>
      ))}
    </ul>
    </article>
  )
}

const StepperFooter = ({ onBack, onNext, nextDisabled = false, nextLabel = 'Siguiente paso' }) => (
  <div className='flex flex-col items-center justify-center gap-4 pt-2 sm:pt-4'>
    <div className='flex w-full flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6'>
      {onBack && (
        <button
          type='button'
          onClick={onBack}
          className='inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-sans font-semibold text-primary shadow-sm transition hover:bg-slate-50 sm:w-auto sm:min-w-[180px]'
        >
          <ArrowLeft className='h-4 w-4' />
          Volver
        </button>
      )}

      <button
        type='button'
        onClick={onNext}
        disabled={nextDisabled}
        className='inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-sans font-semibold text-white shadow-md transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:min-w-[180px]'
      >
        <span>{nextLabel}</span>
        <ArrowRight className='h-4 w-4' />
      </button>
    </div>
  </div>
)

const IntroLeftPanel = ({ isSiiAccepted, onAcceptChange, onContinue, isActivated }) => (
  <div className='space-y-5 lg:space-y-6'>
    <div className='rounded-[24px] border border-accent2/15 bg-gradient-to-br from-accent2/5 via-white to-white p-5 shadow-sm lg:p-6'>
      <div className='mb-4 flex items-center gap-4'>
        <div className='flex h-12 w-12 items-center justify-center rounded-2xl bg-accent2/10 text-accent2'>
          <ShieldAlert className='h-5 w-5' />
        </div>
        <h3 className='text-xl font-sans font-semibold text-slate-900'>
          Importante
        </h3>
      </div>

      <p className='text-sm font-sans leading-6 text-slate-600'>
        Este módulo está diseñado exclusivamente para empresas que utilizan el Facturador Gratuito del SII.
        Si actualmente trabajas con un facturador de mercado (por ejemplo, Defontana, Nubox u otro), este módulo no es compatible contigo.
      </p>
    </div>

    <div className='rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm lg:p-6'>
      <label className='flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-accent2/40'>
        <input
          type='checkbox'
          checked={isSiiAccepted}
          onChange={event => onAcceptChange(event.target.checked)}
          className='mt-1 h-4 w-4 rounded'
        />
        <span className='text-sm font-sans font-medium leading-6 text-slate-700'>
          Si, utilizo el facturador gratuito del SII
        </span>
      </label>

      <p className='mt-5 border-t border-slate-200 pt-4 text-center text-xs font-sans leading-5 text-slate-500'>
        {isActivated
          ? 'La activacion del modulo ya esta disponible para esta empresa.'
          : 'Valida este requisito para continuar con la activacion del modulo.'}
      </p>
    </div>

    <StepperFooter onNext={onContinue} nextDisabled={!isSiiAccepted} />
  </div>
)

const IntroRightPanel = () => (
  <div className='space-y-5 lg:space-y-6'>
    <SidePanelHeader
      eyebrow='Contexto'
      title='¿Qué podras hacer con este modulo?'
      description='Facturacion inteligente integra la emision dentro de TryLuca para reducir tareas manuales y acelerar la operacion diaria.'
      inverse
    />

    <div className='space-y-3 lg:space-y-4'>
      {FEATURE_CARDS.map(card => (
        <InfoCard key={card.title} {...card} inverse />
      ))}
    </div>

    <div className='rounded-[24px] border border-white/15 bg-white/10 p-4 shadow-sm backdrop-blur-md sm:p-5'>
      <p className='mt-2 text-sm font-sans leading-6 text-white/80'>
        Menos tareas repetitivas, menos errores y mucho más tiempo para enfocarte en lo importante.
        Todo directamente desde TryLuca.
      </p>
    </div>
  </div>
)

const ActivationOptionCard = ({ option, onClick }) => (
  <article className='flex h-full flex-col justify-between rounded-[24px] border border-accent2/10 bg-gradient-to-br from-white via-white to-accent2/5 p-5 shadow-sm lg:p-6'>
    <div>
      <p className='text-[11px] font-sans font-semibold uppercase tracking-[0.22em] text-accent2'>
        {option.tag}
      </p>
      <h3 className='mt-3 text-2xl font-sans font-semibold text-slate-900'>{option.title}</h3>
      <p className='mt-3 text-sm font-sans leading-6 text-slate-600'>{option.description}</p>
    </div>

    <div className='mt-5 flex flex-wrap items-center justify-between gap-3'>
      <div className='flex items-center gap-2 text-xs font-sans font-medium uppercase tracking-[0.18em] text-accent2/70'>
        <Sparkles className='h-4 w-4' />
        Pago dentro del flujo
      </div>

      <CustomButton
        title={option.buttonLabel}
        color='accent2'
        className='rounded-full px-5 py-2'
        onClick={onClick}
      />
    </div>
  </article>
)

const ActivationLeftPanel = ({
  isActivated,
  onBack,
  onOpenAddon,
  onOpenPlans,
  onContinueToCredential
}) => (
  <div className='space-y-5 lg:space-y-6'>
    {isActivated ? (
      <div className='rounded-[24px] border border-emerald-200 bg-emerald-50 p-5 shadow-sm lg:p-6'>
        <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
          <div className='flex items-start gap-4'>
            <div className='flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700'>
              <CheckCircle2 className='h-5 w-5' />
            </div>

            <div>
              <h3 className='text-xl font-sans font-semibold text-slate-900'>Modulo activado</h3>
              <p className='mt-2 text-sm font-sans leading-6 text-slate-600'>
                La activacion ya se encuentra lista para esta empresa.
              </p>
            </div>
          </div>
        </div>
      </div>
    ) : (
      <div className='grid gap-4 md:grid-cols-2'>
        {ACTIVATION_OPTIONS.map(option => (
          <ActivationOptionCard
            key={option.key}
            option={option}
            onClick={option.key === 'addon' ? onOpenAddon : onOpenPlans}
          />
        ))}
      </div>
    )}

    <p className='text-center text-sm font-sans leading-6 text-slate-500'>
      {isActivated
        ? 'La activacion ya esta lista para continuar.'
        : 'Completa una activacion para habilitar el siguiente paso.'}
    </p>

    <StepperFooter
      onBack={onBack}
      onNext={onContinueToCredential}
      nextDisabled={!isActivated}
    />
  </div>
)

const ActivationRightPanel = ({ variant }) => {
  const content = ACTIVATION_CONTEXT_CONTENT[variant] || ACTIVATION_CONTEXT_CONTENT.default

  return (
    <div className='space-y-5 lg:space-y-6'>
      <SidePanelHeader
        eyebrow={content.eyebrow}
        title={content.title}
        description={content.description}
        inverse
      />

      <div className='space-y-3 lg:space-y-4'>
        {content.items.map(item => (
          <div
            key={item.title}
            className='rounded-[24px] border border-white/15 bg-white/10 p-4 shadow-sm backdrop-blur-md sm:p-5'
          >
            <p className='text-[11px] font-sans font-semibold uppercase tracking-[0.18em] text-white/70'>
              {item.title}
            </p>
            <p className='mt-2 text-sm font-sans leading-6 text-white/80'>{item.description}</p>
          </div>
        ))}
      </div>

      <div className='rounded-[24px] border border-white/15 bg-white/10 p-4 shadow-sm backdrop-blur-md sm:p-5'>
        <p className='text-sm font-sans leading-6 text-white/80'>{content.footer}</p>
      </div>
    </div>
  )
}

const CredentialLeftPanel = ({ onBack }) => (
  <div className='space-y-5 lg:space-y-6'>
    <CredentialForm
      embedded
      formId='facturacionCredentialForm'
      title='Credenciales del SII'
      description='Ingresa los datos necesarios para conectar esta empresa con el facturador del SII.'
      submitLabel='Siguiente paso'
      secondaryAction={{ label: 'Volver', onClick: onBack }}
    />
  </div>
)

const CredentialRightPanel = () => (
  <div className='space-y-5 lg:space-y-6'>
    <SidePanelHeader
      eyebrow='Credenciales'
      title='Datos que necesitaras para este paso'
      description='Reune esta informacion antes de enviar el formulario para completar la conexion sin interrupciones.'
      inverse
    />

    <div className='space-y-3 lg:space-y-4'>
      {CREDENTIAL_INFO_ITEMS.map(item => (
        <div
          key={item.title}
          className='rounded-[24px] border border-white/15 bg-white/10 p-4 shadow-sm backdrop-blur-md sm:p-5'
        >
          <p className='text-[11px] font-sans font-semibold uppercase tracking-[0.18em] text-white/70'>
            {item.title}
          </p>
          <p className='mt-2 text-sm font-sans leading-6 text-white/80'>{item.description}</p>
        </div>
      ))}
    </div>

    <div className='rounded-[24px] border border-white/15 bg-white/10 p-4 shadow-sm backdrop-blur-md sm:p-5'>
      <div className='flex items-start gap-3'>
        <div className='flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15 text-white'>
          <ShieldAlert className='h-5 w-5' />
        </div>

        <div>
          <p className='text-[11px] font-sans font-semibold uppercase tracking-[0.18em] text-white/70'>
            Antes de enviar
          </p>
          <p className='mt-2 text-sm font-sans leading-6 text-white/80'>
            Verifica que el certificado digital siga vigente y que las claves correspondan a la
            empresa con la que estas trabajando.
          </p>
        </div>
      </div>
    </div>
  </div>
)

export default function FacturacionOnboarding({ isActivated }) {
  const swiperRef = useRef(null)
  const previousActivationRef = useRef(isActivated)
  const [activeStep, setActiveStep] = useState(STEP_INDEX.intro)
  const [isSiiAccepted, setIsSiiAccepted] = useState(false)
  const [showChangePlanModal, setShowChangePlanModal] = useState(false)
  const [showAddonModal, setShowAddonModal] = useState(false)
  const [activationContext, setActivationContext] = useState(isActivated ? 'existing' : null)

  const totalSteps = ONBOARDING_STEPS.length
  const currentStepNumber = activeStep + 1
  const activeStepMeta = ONBOARDING_STEPS[activeStep]
  const hideRightPanel = activeStep === STEP_INDEX.activation
  const activationInfoVariant = isActivated
    ? activationContext || 'existing'
    : activationContext || 'default'

  const getOnboardingSwiper = swiper => {
    const currentSwiper = swiper ?? swiperRef.current

    if (!currentSwiper || currentSwiper.destroyed || !currentSwiper.params) {
      return null
    }

    return currentSwiper
  }

  const goToStep = step => {
    getOnboardingSwiper()?.slideTo(step)
    setActiveStep(step)
  }

  const updateSwiperLayout = swiper => {
    requestAnimationFrame(() => {
      const activeSwiper = getOnboardingSwiper(swiper)

      if (!activeSwiper) return

      activeSwiper.updateAutoHeight?.(0)
      activeSwiper.update?.()
    })
  }

  const handleIntroContinue = () => {
    if (!isSiiAccepted) return

    if (isActivated) {
      goToStep(STEP_INDEX.credential)
      return
    }

    goToStep(STEP_INDEX.activation)
  }

  const handleOpenAddon = () => {
    setActivationContext('addon')
    setShowAddonModal(true)
  }

  const handleOpenPlans = () => {
    setActivationContext('plan')
    setShowChangePlanModal(true)
  }

  useEffect(() => {
    if (
      activeStep === STEP_INDEX.activation &&
      isActivated &&
      previousActivationRef.current === false
    ) {
      goToStep(STEP_INDEX.credential)
    }

    previousActivationRef.current = isActivated
  }, [activeStep, isActivated])

  useEffect(() => {
    if (isActivated && !activationContext) {
      setActivationContext('existing')
    }

    if (!isActivated && activationContext === 'existing') {
      setActivationContext(null)
    }
  }, [activationContext, isActivated])

  useEffect(() => {
    updateSwiperLayout()
  }, [activeStep, activationContext, isActivated, isSiiAccepted])

  const renderRightPanel = () => {
    if (activeStep === STEP_INDEX.intro) {
      return <IntroRightPanel />
    }

    if (activeStep === STEP_INDEX.activation) {
      return <ActivationRightPanel variant={activationInfoVariant} />
    }

    return <CredentialRightPanel />
  }

  return (
    <>
      <div
        className='flex flex-col rounded-3xl bg-white p-4 shadow-md'
        style={{ height: 'calc(100vh - 7rem)' }}
      >
        <div
          className={`grid min-h-0 flex-1 gap-4 overflow-hidden ${
            hideRightPanel ? 'grid-cols-1' : 'lg:grid-cols-[minmax(0,1.45fr)_minmax(360px,0.95fr)]'
          }`}
        >
          <section className='min-h-0 rounded-[28px] border border-slate-200 bg-white p-4 sm:p-5 lg:p-6'>
            <div className='flex h-full min-h-0 flex-col gap-5 lg:gap-6'>
              <StepHeader
                stepNumber={currentStepNumber}
                totalSteps={totalSteps}
                stepMeta={activeStepMeta}
              />

              <div className='min-h-0 flex-1 overflow-y-auto pr-1'>
                <Swiper
                  autoHeight
                  allowTouchMove={false}
                  observer
                  observeParents
                  onSwiper={swiper => {
                    swiperRef.current = swiper
                    updateSwiperLayout(swiper)
                  }}
                  onSlideChange={swiper => {
                    if (!swiper || swiper.destroyed || !swiper.params) return

                    setActiveStep(swiper.activeIndex)
                    updateSwiperLayout(swiper)
                  }}
                  className='min-h-0 h-full'
                >
                  <SwiperSlide>
                    <div className='pr-0 lg:pr-1'>
                      <IntroLeftPanel
                        isSiiAccepted={isSiiAccepted}
                        onAcceptChange={setIsSiiAccepted}
                        onContinue={handleIntroContinue}
                        isActivated={isActivated}
                      />
                    </div>
                  </SwiperSlide>

                  <SwiperSlide>
                    <div className='pr-0 lg:pr-1'>
                      <ActivationLeftPanel
                        isActivated={isActivated}
                        onBack={() => goToStep(STEP_INDEX.intro)}
                        onOpenAddon={handleOpenAddon}
                        onOpenPlans={handleOpenPlans}
                        onContinueToCredential={() => goToStep(STEP_INDEX.credential)}
                      />
                    </div>
                  </SwiperSlide>

                  <SwiperSlide>
                    <div className='pr-0 lg:pr-1'>
                      <CredentialLeftPanel onBack={() => goToStep(STEP_INDEX.activation)} />
                    </div>
                  </SwiperSlide>
                </Swiper>
              </div>
            </div>
          </section>

          {!hideRightPanel && (
            <aside className='relative min-h-0 overflow-hidden rounded-[28px] bg-gradient-to-br from-primary via-primary to-accent2 text-white'>
              <div className='absolute inset-0 bg-gradient-to-br from-primary via-primary to-accent2' />
              <div className='absolute right-[-16%] top-[-10%] h-64 w-64 rounded-full bg-blue-400/25 blur-3xl' />
              <div className='absolute bottom-[-16%] left-[-12%] h-64 w-64 rounded-full bg-white/10 blur-3xl' />

              <div className='relative z-10 h-full overflow-y-auto px-4 py-5 sm:px-5 sm:py-6 lg:px-6 lg:py-5 xl:px-8 xl:py-6'>
                <div className='flex min-h-full w-full flex-col justify-start gap-5 lg:gap-6 xl:justify-center'>
                  {renderRightPanel()}
                </div>
              </div>
            </aside>
          )}
        </div>
      </div>

      <ChangePlanModal
        isOpen={showChangePlanModal}
        onClose={() => setShowChangePlanModal(false)}
        onPlanUpdated={() => setShowChangePlanModal(false)}
        filterPlanBy={{ permission: 'allowIntelligentBilling' }}
      />

      <ContractAddonsModal
        isOpen={showAddonModal}
        onClose={() => setShowAddonModal(false)}
        onSuccess={() => setShowAddonModal(false)}
        preferredAddonPermission='allowIntelligentBilling'
      />
    </>
  )
}
